import { useNavigation } from '@react-navigation/native';
import React, { forwardRef, useImperativeHandle } from 'react';
import { ScrollView } from 'react-native';
import Button from 'src/components/10-atoms/button/Button';
import HTMLEditor from 'src/components/10-atoms/form/inputs/editor/html/HTMLEditor/Editor';
import HTMLRenderer from 'src/components/10-atoms/form/inputs/editor/html/HTMLEditor/Renderer';
import FeatherIcon from 'src/components/10-atoms/icons/FeatherIcon';
import Column from 'src/components/10-atoms/layout/Column';
import Row from 'src/components/10-atoms/layout/Row';
import BackdropLoader from 'src/components/10-atoms/loaders/BackdropLoader';
import { PRESSABLE_SIZE } from 'src/components/10-atoms/pressables/constants';
import Tab from 'src/components/10-atoms/tabs/Tab';
import Tabs from 'src/components/10-atoms/tabs/Tabs';
import { overlayHelperViewActions } from 'src/components/20-helper-views/overlay/useOverlay';
import useTheme from 'src/customizations/themes/hooks/useTheme';
import createThemedStyle from 'src/customizations/themes/utils/createThemedStyles';
import useI18n from 'src/modules/translations/hooks/useI18n';
import styleUtils from 'src/utils/styles';
import NoteTabNavButton from './NoteTabNavButton';
import NoteTabs from './NoteTabs';
import useData from './useData';
const NoteEditorContainer = forwardRef(({ note, editable = true, showHistoryAction = true, ...columnProps }, ref) => {
    const [{ deviceType, dimensions, theme, styles }] = useTheme(stylesGenerator);
    const { translate } = useI18n();
    const [state, actions] = useData(note);
    const navigation = useNavigation();
    useImperativeHandle(ref, () => ({
        save: actions.save,
        hasUnsavedChanges: actions.hasUnsavedChanges,
    }));
    const isSaving = state.syncingTabIDs.size > 0;
    const handleViewHistory = (tab) => {
        navigation.navigate('Admin_NoteTabHistory', {
            noteID: note.id,
            noteTabID: tab.id,
        });
    };
    const onViewHistory = showHistoryAction ? handleViewHistory : undefined;
    return (React.createElement(Column, { flex: true, ...columnProps },
        React.createElement(Row, { flex: true },
            !deviceType.isPhone && (React.createElement(Column, { style: { width: 280 } },
                React.createElement(NoteTabs, { style: { borderRightWidth: 1 }, tabs: note.tabs, editable: editable, activeTabID: state.activeTabID, onAddTab: actions.addTab, onTabChange: actions.setActiveTabID, onEditTab: actions.editTab, onDeleteTab: actions.deleteTab, onViewHistory: onViewHistory }))),
            React.createElement(Tabs, { activeTab: state.activeTabID, behaviour: "hide", childContainerProps: { flex: true } }, note.tabs.map((tab, index) => {
                const content = state.draft.get(tab.id) ?? tab.data.content ?? '';
                const previousTab = note.tabs[index - 1];
                const nextTab = note.tabs[index + 1];
                return (React.createElement(Tab, { key: tab.id, id: tab.id },
                    React.createElement(Column, { flex: true }, !editable
                        ? (React.createElement(ScrollView, { contentContainerStyle: styleUtils.flex },
                            React.createElement(Column, { ph: theme.spacing.page, pt: theme.spacing.standard.md, pb: theme.spacing.standard.xl, spacingY: theme.spacing.standard.md },
                                previousTab && (React.createElement(NoteTabNavButton, { direction: "previous", title: previousTab.data.title, onPress: () => actions.setActiveTabID(previousTab.id) })),
                                React.createElement(HTMLRenderer, { html: content }),
                                React.createElement(Column, { style: { height: 50 } }),
                                nextTab && (React.createElement(NoteTabNavButton, { direction: "next", title: nextTab.data.title, onPress: () => actions.setActiveTabID(nextTab.id) })))))
                        : (React.createElement(HTMLEditor, { flex: true, initialValue: content, onChange: editable ? ({ html }) => actions.draft.setTabContent(tab.id, html) : undefined })))));
            }))),
        deviceType.isPhone && (React.createElement(Button, { style: { bottom: PRESSABLE_SIZE + theme.spacing.page }, variant: 'float', onPress: () => {
                overlayHelperViewActions.show({
                    component: React.createElement(NoteTabs, { tabs: note.tabs, editable: editable, activeTabID: state.activeTabID, onTabChange: (tabID) => {
                            actions.setActiveTabID(tabID);
                            overlayHelperViewActions.hide();
                        }, onAddTab: () => {
                            actions.addTab();
                            overlayHelperViewActions.hide();
                        }, onEditTab: (tab) => {
                            actions.editTab(tab);
                            overlayHelperViewActions.hide();
                        }, onDeleteTab: (tab) => {
                            actions.deleteTab(tab);
                            overlayHelperViewActions.hide();
                        }, onViewHistory: onViewHistory ? (tab) => {
                            handleViewHistory(tab);
                            overlayHelperViewActions.hide();
                        } : undefined }),
                    direction: 'from-right',
                    style: { width: Math.min(dimensions.device.width - 72, 280) },
                });
            } },
            React.createElement(FeatherIcon, { name: "menu" }))),
        isSaving && React.createElement(BackdropLoader, null)));
});
const stylesGenerator = createThemedStyle(({ theme }) => ({}));
export default NoteEditorContainer;
